/**
 * reCAPTCHA action recorded on the token generated for online ordering payment
 * intent creation.
 */
export const RECAPTCHA_ACTION_CREATE_PAYMENT_INTENT = 'SpiCreatePaymentIntent'

/**
 * reCAPTCHA action recorded on the token generated for 1P gift card balance
 * inquiries.
 */
export const RECAPTCHA_ACTION_GIFT_CARD_INQUIRY = 'giftcard_inquiry'

/** Identifies the call site that triggered a reCAPTCHA token generation failure. */
export enum ReCaptchaSource {
  PAYMENT_INTENT = 'paymentIntent',
  GIFT_CARD_INQUIRY_1P = 'giftCardInquiry1P'
}

/**
 * Ceiling on how long we wait for a single `grecaptcha.enterprise` attempt to
 * produce a token, one entry per attempt.
 *
 * The budget steps up rather than staying flat: a short first attempt abandons a
 * wedged call quickly, since retrying usually beats waiting it out, while the
 * later, longer attempts still cover a genuinely slow network that just needs
 * more room. The array length is what defines how many attempts we make — see
 * RECAPTCHA_TOKEN_MAX_ATTEMPTS.
 */
export const RECAPTCHA_TOKEN_ATTEMPT_TIMEOUTS_MS = [3000, 5000, 7000] as const

/**
 * How many times we attempt token generation before giving up and falling open.
 * Derived from the per-attempt budgets above, so the two can't drift apart.
 */
export const RECAPTCHA_TOKEN_MAX_ATTEMPTS =
  RECAPTCHA_TOKEN_ATTEMPT_TIMEOUTS_MS.length

/**
 * How long to wait before retrying after a `ReCaptchaUnavailableError`.
 *
 * That failure is synchronous — the library simply isn't on the page yet — so
 * retrying immediately would just re-read the same missing global and burn every
 * attempt in a few milliseconds. Waiting gives a slow-loading script time to
 * arrive. Only applied between attempts, never after the last one.
 */
export const RECAPTCHA_UNAVAILABLE_RETRY_DELAY_MS = 1500

/** reCAPTCHA Enterprise tokens are valid for 2 minutes after generation. */
export const RECAPTCHA_TOKEN_EXPIRY_MS = 2 * 60 * 1000

/**
 * How early to treat a token as expired before the server-side deadline.
 * Accounts for network round-trip and backend processing time so a token
 * that passes the client check doesn't expire in transit.
 */
export const RECAPTCHA_TOKEN_EXPIRY_BUFFER_MS = 15_000
